/** ========================================================================
 * Mixin usado nas páginas de flutuações tarifárias para validação dos campos
 * ========================================================================= */
import { formatDate } from '@/helpers/date'
import { removeStringAccents } from '@/helpers/string'

export default {
  data() {
    return {
      basicValidatedItens: [],
      pricingValidatedItens: [],
      periodsValidatedItens: [],
      restrictionsValidatedItens: [],
      policiesValidatedItens: [],

      selectedDays: [],

      weekDaysOptions: [
        { text: 'Domingo', value: 1 },
        { text: 'Segunda', value: 2 },
        { text: 'Terça', value: 3 },
        { text: 'Quarta', value: 4 },
        { text: 'Quinta', value: 5 },
        { text: 'Sexta', value: 6 },
        { text: 'Sábado', value: 7 }
      ]
    }
  },

  methods: {
    /** Tratamento de itens a serem mostrados na validação
     * @param {String} variable - Variavel a ser adicionado o valor
     * @param {String} objectVariable - Variavel do objeto apiObjectData
     * @param {String} startsWith - Titulo do item a ser validado
     * @param {String} value - Valor a ser adicionado no item
     */
    async handleItemWithValue(variable, objectVariable, startsWith, value) {
      /* Validação padrão */
      const index = this[variable]?.findIndex((item) =>
        item.startsWith(startsWith)
      )

      if (index === -1 && this.apiObjectData[objectVariable]) {
        this[variable]?.push(value)
      } else if (index !== -1 && !this.apiObjectData[objectVariable]) {
        this[variable]?.splice(index, 1)
      }
    },

    /* Valida valores de coeficiente */
    handleCoeficient() {
      const hasCoeficiente = this.apiObjectData.coeficiente
      const hasPrecosQuartos = this.apiObjectData.precosQuartos?.length > 0
      const hasCoeficienteDiasSemana =
        this.apiObjectData.coeficienteDiasSemana?.length > 0

      if (hasCoeficiente && !hasPrecosQuartos && !hasCoeficienteDiasSemana) {
        this.removeOtherItems([
          'Calendário absoluto',
          'Coeficiente por dia da semana'
        ])
        this.basicValidatedItens?.push('Coeficiente')
      }

      if (
        hasPrecosQuartos &&
        (!hasCoeficiente || hasCoeficiente) &&
        !hasCoeficienteDiasSemana
      ) {
        this.removeOtherItems(['Coeficiente', 'Coeficiente por dia da semana'])
        this.basicValidatedItens?.push('Calendário absoluto')
      }

      if (
        hasCoeficienteDiasSemana &&
        (!hasCoeficiente || hasCoeficiente) &&
        !hasPrecosQuartos
      ) {
        this.removeOtherItems(['Calendário absoluto', 'Coeficiente'])
        this.basicValidatedItens?.push('Coeficiente por dia da semana')
      }
    },

    removeOtherItems(itemsToRemove) {
      itemsToRemove.forEach((itemToRemove) => {
        const index = this.basicValidatedItens?.indexOf(itemToRemove)
        if (index !== -1) {
          this.basicValidatedItens?.splice(index, 1)
        }
      })
    },

    /** Validação de idiomas
     * @param {String} variable - Variavel a ser adicionado o valor
     * @param {String} objectVariable - Variavel do objeto
     * @param {String} text - Texto que antecede os idiomas preenchidos
     */
    handleGeralLanguage(variable, objectVariable, text) {
      const idiomaNomes = {
        'pt-BR': 'Português',
        'en-US': 'Inglês',
        'es-ES': 'Espanhol'
      }

      const idiomasExibicao = []

      this.apiObjectData.idiomas.forEach((idioma) => {
        if (idioma[objectVariable] !== '') {
          idiomasExibicao.push(idioma.idioma)
        }
      })

      if (idiomasExibicao?.length > 0) {
        const idiomasNomes = idiomasExibicao.map(
          (codigo) => idiomaNomes[codigo]
        )
        const idiomasString = idiomasNomes.join(', ')
        const novoNome = `${text} ${idiomasString}`

        /*  Procurar e substituir o item anterior */
        const index = this[variable]?.findIndex((item) => item.startsWith(text))

        if (index !== -1) {
          this[variable]?.splice(index, 1, novoNome)
        } else {
          /*  Se não encontrou, apenas adicionar o novo item */
          this[variable]?.push(novoNome)
        }
      }
    },

    /* Validação da parte de Ativação Básica */
    basicItensValidation() {
      this.basicValidatedItens = []

      /* Valida Nome de exibição para hóspedes */
      this.handleItemWithValue(
        'basicValidatedItens',
        'nome',
        'Nome interno:',
        `Nome interno: ${this.apiObjectData?.nome}`
      )

      /* Valida idiomas */
      this.handleGeralLanguage(
        'basicValidatedItens',
        'nome',
        'Idiomas de exibição:'
      )

      /* Valida tarifa prioritária*/
      this.handleItemWithValue(
        'basicValidatedItens',
        'prioritaria',
        'Tarifa prioritária',
        'Tarifa prioritária'
      )

      /* Valida código PMS */
      this.handleItemWithValue(
        'basicValidatedItens',
        'codigoPMS',
        'Código PMS',
        `Código PMS: ${this.apiObjectData?.codigoPMS}`
      )

      /* Valida coeficiente */
      this.handleCoeficient()

      /* Valida exibir desconto */
      this.handleItemWithValue(
        'basicValidatedItens',
        'exibeDesconto',
        'Exibir desconto',
        'Exibir desconto'
      )
    },

    /** Tratamento de itens de arrays a serem validados
     * @param {String} variable - Variavel a ser adicionado o valor
     * @param {String} objectVariable - Variavel do objeto apiObjectData
     * @param {String} text - Texto do item a ser validado
     */
    handleItemWithLength(variable, objectVariable, text) {
      /* Valida tarifa prioritaria */
      if (
        this.apiObjectData[objectVariable]?.length &&
        !this[variable]?.includes(text)
      ) {
        this[variable]?.push(text)
      } else if (
        !this.apiObjectData[objectVariable]?.length &&
        this[variable]?.includes(text)
      ) {
        const index = this[variable]?.findIndex((item) => item === text)

        this[variable]?.splice(index, 1)
      }
    },

    /* Validação dos campos de configuração de precificação */
    pricingItensValidation() {
      this.pricingValidatedItens = []

      /* Valida pensão por idade */
      this.handleItemWithLength(
        'pricingValidatedItens',
        'pensoes',
        'Pensão por idade'
      )

      /* Valida noite grátis*/
      this.handleItemWithValue(
        'pricingValidatedItens',
        'utilizaNoitesGratis',
        'Noite grátis',
        'Noite grátis'
      )

      /* Valida pax adicional */
      this.handleItemWithValue(
        'pricingValidatedItens',
        'diferenciaisPax',
        'Acréscimo por pax adicional',
        'Acréscimo por pax adicional'
      )

      /* Valida Valores por acomodações */
      this.handleItemWithValue(
        'pricingValidatedItens',
        'diferenciaisQuartos',
        'Valores por acomodações',
        'Valores por acomodações'
      )
    },

    periodsItensValidation() {
      this.periodsValidatedItens = []

      /* Valida data inicial*/
      if (this.apiObjectData?.dataInicioReserva)
        this.handleItemWithValue(
          'periodsValidatedItens',
          'dataInicioReserva',
          'Data inicial',
          `Data inicial: ${formatDate(
            new Date(this.apiObjectData?.dataInicioReserva),
            'dd/MM/yyyy - HH:mm'
          )}h`
        )

      /* Valida data final*/
      if (this.apiObjectData?.dataFimReserva)
        this.handleItemWithValue(
          'periodsValidatedItens',
          'dataFimReserva',
          'Data final',
          `Data final: ${formatDate(
            this.apiObjectData?.dataFimReserva,
            'dd/MM/yyyy - HH:mm',
            true
          )}h`
        )

      /* Valida horários de ativação recorrente  */
      if (
        this.apiObjectData?.horaInicioReserva !== '00:00' ||
        this.apiObjectData?.horaFimReserva !== '23:59'
      ) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'horaInicioReserva',
          'Ativação recorrente',
          `Horário de ativação recorrente: ${this.apiObjectData
            ?.horaInicioReserva +
            'h - ' +
            this.apiObjectData?.horaFimReserva}h`
        )
      }

      /* Valida periodos de hospedagem */
      if (this.apiObjectData.periodosHospedagem?.length) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'periodosHospedagem',
          'Período de hospedagem',
          'Período de hospedagem'
        )
      }

      /* Valida data obrigatória para hospedagem */
      if (this.apiObjectData.dataPernoiteObrigatorio) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'dataPernoiteObrigatorio',
          'Data obrigatória para hospedagem',
          `Data obrigatória para hospedagem: ${formatDate(
            this.apiObjectData?.dataPernoiteObrigatorio,
            'dd/MM/yyyy',
            true
          )}`
        )
      }

      /* Valida mínimo de noites */
      if (this.apiObjectData.minimoNoitesDe) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'minimoNoitesDe',
          'Mínimo de noites',
          `Mínimo de noites: ${this.apiObjectData.minimoNoitesDe} noites`
        )
      }

      /* Valida máximo de noites */
      if (this.apiObjectData.minimoNoitesAte) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'minimoNoitesAte',
          'Máximo de noites',
          `Máximo de noites: ${this.apiObjectData.minimoNoitesAte} noites`
        )
      }

      /* Valida período de destaque */
      if (this.apiObjectData.dataInicioPeriodoDestaque) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'dataInicioPeriodoDestaque',
          'Período de destaque',
          'Período de destaque'
        )
      }

      /* Valida dias de checkin flexivel */
      if (this.apiObjectData.diasToleranciaCheckin) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'diasToleranciaCheckin',
          'Dias de Check-in flexível',
          `Dias de Check-in flexível: ${this.apiObjectData.diasToleranciaCheckin} dias`
        )
      }

      /* Valida coeficiente do checkin flexivel */
      if (this.apiObjectData.coeficienteCheckin) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'coeficienteCheckin',
          'Coeficiente do Check-in flexível',
          `Coeficiente do Check-in flexível: ${this.apiObjectData.coeficienteCheckin}%`
        )
      }

      /* Valida dias de checkout flexivel */
      if (this.apiObjectData.diasToleranciaCheckout) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'diasToleranciaCheckout',
          'Dias de Check-out flexível',
          `Dias de Check-out flexível: ${this.apiObjectData.diasToleranciaCheckout} dias`
        )
      }

      /* Valida coeficiente do checkout flexivel */
      if (this.apiObjectData.coeficienteCheckout) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'coeficienteCheckout',
          'Coeficiente do Check-out flexível',
          `Coeficiente do Check-out flexível: ${this.apiObjectData.coeficienteCheckout}%`
        )
      }

      /* Valida periodo de blackout */
      if (this.apiObjectData.periodosBlackout?.length) {
        this.handleItemWithValue(
          'periodsValidatedItens',
          'periodosBlackout',
          'Período de blackout',
          'Período de blackout'
        )
      }
    },

    /* Valida dias da semana */
    validateDaysOfWeek() {
      this.selectedDays = []

      this.selectedDays = this.weekDaysOptions
        .filter(
          (day) => this.apiObjectData[`checkin${removeStringAccents(day.text)}`]
        )
        .map((day) => day.text)

      if (this.selectedDays?.length === this.weekDaysOptions?.length) {
        return 'Todos'
      } else {
        return this.selectedDays.join(', ')
      }
    },

    /* Valida allotments */
    validateAllotmentField() {
      const validatedAllotment = this.apiObjectData.allotmentQuartos?.filter(
        (item) => {
          return item.allotment !== null
        }
      )

      const rooms = []
      if (validatedAllotment?.length > 0 && this.hotelRoomsData?.length) {
        validatedAllotment.forEach((allotment) => {
          const matchingRoom = this.hotelRoomsData.find(
            (room) => room.codigo === allotment.codigoQuarto
          )

          if (matchingRoom) {
            rooms.push({
              codigoQuarto: allotment.codigoQuarto,
              hotelName: matchingRoom.nome
            })
          }
        })
      }

      let hotelNames = null

      if (rooms?.length > 0) {
        hotelNames = rooms.map((room) => room.hotelName).join(', ')
      }

      return hotelNames
    },

    /* Cria texto de validação de dispositivos */
    generateDevicesText(devices) {
      const desktopText =
        devices.includes(0) || devices.includes('0') ? 'Desktop' : ''
      const mobileText =
        devices.includes(1) || devices.includes('1') ? 'Mobile' : ''

      if (desktopText && mobileText) {
        return `Dispositivos: ${desktopText} e ${mobileText}`
      } else if (desktopText) {
        return `Dispositivos: ${desktopText}`
      } else if (mobileText) {
        return `Dispositivos: ${mobileText}`
      } else {
        return ''
      }
    },

    /* Validação dos campos de restrições */
    async restrictionsItensValidation() {
      /* Chamada dos quartos de hoteis */
      await this.getHotelRoomsData()

      this.restrictionsValidatedItens = []

      if (
        this.apiObjectData.quartos &&
        this.apiObjectData.tipoCoeficiente === 0
      ) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'quartos',
          'Apartamentos',
          'Apartamentos'
        )
      }

      /* Valida allotments */
      if (
        this.apiObjectData.allotmentQuartos?.length &&
        this.validateAllotmentField() !== null
      ) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'allotmentQuartos',
          'Allotment',
          `Allotments: ${this.validateAllotmentField()}`
        )
      }

      /* Valida dispositivos */
      if (this.apiObjectData.devices?.length) {
        const devicesText = this.generateDevicesText(this.apiObjectData.devices)
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'devices',
          'Dispositivos',
          devicesText
        )
      }

      /* Valida dias da semana */
      if (this.validateDaysOfWeek()) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          `checkin${this.selectedDays[0]}`,
          'Dias da semana',
          `Dias da semana: ${this.validateDaysOfWeek()}`
        )
      }

      /* Valida antecedencia mínima */
      if (this.apiObjectData.antecedenciaDe) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'antecedenciaDe',
          'Antecedência miníma',
          `Antecedência miníma: ${this.apiObjectData.antecedenciaDe}`
        )
      }

      /* Valida antecedencia máxima */
      if (this.apiObjectData.antecedenciaAte) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'antecedenciaAte',
          'Antecedência máxima',
          `Antecedência máxima: ${this.apiObjectData.antecedenciaAte}`
        )
      }

      /* Valida hora de ativação */
      if (this.apiObjectData.lastMinute) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'lastMinute',
          'Hora de ativação',
          `Hora de ativação: ${this.apiObjectData.lastMinute}`
        )
      }

      /* Valida descrição do last minute */
      if (this.apiObjectData.lastMinuteDescricao) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'lastMinuteDescricao',
          'Descrição last minute',
          'Descrição last minute'
        )
      }

      /*  Validação de minimoAdultos */
      if (this.apiObjectData.minimoAdultos) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'minimoAdultos',
          'Mínimo de adultos',
          `Mínimo de adultos: ${this.apiObjectData.minimoAdultos}`
        )
      }

      /*  Validação de maximoAdultos */
      if (this.apiObjectData.maximoAdultos) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'maximoAdultos',
          'Máximo de adultos',
          `Máximo de adultos: ${this.apiObjectData.maximoAdultos}`
        )
      }

      /* Validação de minimoCriancas */
      if (this.apiObjectData.minimoCriancas) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'minimoCriancas',
          'Mínimo de crianças',
          `Mínimo de crianças: ${this.apiObjectData.minimoCriancas}`
        )
      }

      /*  Validação de maximoCriancas */
      if (this.apiObjectData.maximoCriancas) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'maximoCriancas',
          'Máximo de crianças',
          `Máximo de crianças: ${this.apiObjectData.maximoCriancas}`
        )
      }

      /* Valida Código de tarifa */
      if (this.apiObjectData.promocodes?.length) {
        this.handleItemWithLength(
          'restrictionsValidatedItens',
          'promocodes',
          'Código de tarifa'
        )
      }

      /* Valida minimo de ocupação */
      if (this.apiObjectData.percentualOcupacaoMinimo) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'percentualOcupacaoMinimo',
          'Mínimo de ocupação',
          `Mínimo de ocupação: ${this.apiObjectData.percentualOcupacaoMinimo}%`
        )
      }

      /* Valida máximo de ocupação */
      if (this.apiObjectData.percentualOcupacaoMaximo) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'percentualOcupacaoMaximo',
          'Máximo de ocupação',
          `Máximo de ocupação: ${this.apiObjectData.percentualOcupacaoMaximo}%`
        )
      }

      /* Valida máximo de ocupação */
      if (this.apiObjectData.urlImagemPromocional) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'urlImagemPromocional',
          'Foto da promoção',
          'Foto da promoção'
        )
      }

      /* Valida máximo de ocupação */
      if (this.apiObjectData.gruposCrm?.length) {
        this.handleItemWithValue(
          'restrictionsValidatedItens',
          'gruposCrm',
          'Grupos CRM',
          'Grupos CRM'
        )
      }

      /* Valida idiomas de descrição */
      if (this.apiObjectData.idiomas?.length) {
        this.handleGeralLanguage(
          'restrictionsValidatedItens',
          'pensao',
          'Idiomas de descrição:'
        )
      }
    },

    async policiesItensValidation() {
      this.policiesValidatedItens = []

      /* Valida diferenciais da flutuação */
      if (this.apiObjectData.destaques?.length) {
        this.handleItemWithValue(
          'policiesValidatedItens',
          'destaques',
          'Diferencias da flutuação',
          'Diferencias da flutuação'
        )
      }

      if (this.apiObjectData.idiomas?.length) {
        /* Valida Descrição promoção da flutuação */
        this.handleGeralLanguage(
          'policiesValidatedItens',
          'descricaoPromocao',
          'Idiomas de itens inclusos:'
        )

        /* Valida Descrição da politica */
        this.handleGeralLanguage(
          'policiesValidatedItens',
          'politica',
          'Idiomas de políticas:'
        )
      }

      /* Valida modalidade de cobrança */
      if (this.apiObjectData.meiosPagamento?.length) {
        this.handleItemWithValue(
          'policiesValidatedItens',
          'meiosPagamento',
          'Modalidade de cobrança',
          'Modalidade de cobrança'
        )
      }

      /* Valida formas de pagamento */
      if (this.apiObjectData.formasPagamento?.length) {
        await this.loadPaymentForms()

        this.handleItemWithValue(
          'policiesValidatedItens',
          'formasPagamento',
          'Formas de pagamento:',
          `Formas de pagamento: ${this.validatePaymentForms()}`
        )
      }

      /* Valida forma de pagamento exclusiva */
      if (this.apiObjectData.formasPagamentoExclusivoFlutuacao) {
        this.handleItemWithValue(
          'policiesValidatedItens',
          'formasPagamentoExclusivoFlutuacao',
          'Forma de pagamento exclusivo flutuação',
          'Forma de pagamento exclusivo flutuação'
        )
      }

      if (this.apiObjectData.formasPagamento?.length) {
        await this.loadPaymentForms()

        this.handleItemWithValue(
          'policiesValidatedItens',
          'formasPagamento',
          'Formas de pagamento:',
          `Formas de pagamento: ${this.validatePaymentForms()}`
        )
      }

      if (
        this.hotelData?.pixHabilitadoTodasTarifas ||
        this.apiObjectData?.habilitarPagamentoPix
      ) {
        this.handleItemWithValue(
          'policiesValidatedItens',
          'habilitarPagamentoPix',
          'Tarifa com pagamento PIX',
          'Tarifa com pagamento PIX'
        )
      }
    },

    /* Retorna as formas de pagamento selecionadas */
    validatePaymentForms() {
      return this.hotelPaymentForms
        .filter((payment) =>
          this.apiObjectData.formasPagamento.includes(payment.codigo)
        )
        .map((payment) => payment.nome)
        .join(', ')
    }
  }
}
